import moment from "moment"
import React, { useEffect, useState } from "react"
import { Alert, Badge, Form, Tab, Tabs } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import ScaleLoader from "react-spinners/ScaleLoader"
import api from "../../api"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import DateRangePickerCustom from "../../components/Common/DateRangePicker/DateRangePickerCustom"
import { GLOBAL_CONSTANTS } from "../../constants/constants"
import { RootState } from "../../store"
import {
    loadCreditParcels,
    loadMarkReasons,
    loadParcelDestinations,
    loadParcelStatuses,
    supplierAutocomplete
} from "../../store/parcel.store"
import Header from "../Layout/Header"
import CreditParcelsTable from "./CreditParcelsTable"
import InvoicedCreditParcels from "./InvoicedCreditParcels"

const CreditParcels = () => {
    const dispatch = useAppDispatch()
    //Date Picker Logic
    const { t } = useTranslation()
    const { userInfo } = useAppSelector((state: RootState) => state.auth)
    const { creditParcels, creditInvoicedParcels, loading, parcelStatus } = useAppSelector(
        (state: RootState) => state.parcels
    )
    const { customers, loading: customersLoading } = useAppSelector((state: RootState) => state.customers)

    //Date Picker Logic
    const [startDate, setStartDate] = useState("")
    const [endDate, setEndDate] = useState("")
    const [show, setShow] = useState(false)
    const [page, setpage] = useState(GLOBAL_CONSTANTS.PAGE)
    const [pageSize, setPageSize] = useState(GLOBAL_CONSTANTS.PAGE_SIZE)
    const [warehouse, setWarehouse] = useState("")
    const [customer, setCustomerId] = useState()
    const [invoice_type, setInvoiceType] = useState(1)
    const [selectedRows, setSelectedRows] = useState([] as number[])
    const [CustomerList, setCustomerList] = useState([] as Record<string, any>)

    const [parcelSearch, setParcelSearch] = useState({
        uuid: "",
        customer: "",
        country: "",
        suite: "",
        status: {
            value: "",
            label: ""
        },
        date: "",
        split: "",
        deleted: ""
    })
    const [error, setError] = useState("")
    const [buttonId, setButtonId] = useState("")

    const defaultWarehouse = userInfo?.ParcelHubUserWarehouse?.find(
        (uw: Record<string, any>) => uw?.is_default
    )?.warehouse_id
    const defaultInvoice = 0
    useEffect(() => {
        setWarehouse(`${defaultWarehouse}`)
        setShow(false)
    }, [defaultWarehouse])

    useEffect(() => {
        setInvoiceType(defaultInvoice)
        setShow(false)
    }, [defaultInvoice])

    const tableDataChange = (page: number, page_size: number) => {
        setpage(page)
        setPageSize(page_size)
    }

    const handleBtnClick = async () => {
        dispatch(
            loadCreditParcels({
                page,
                pageSize,
                warehouse,
                invoice_type,
                customer,
                parcelSearch
            })
        )
    }

    const handleOnSelect = (row: any, isSelect: boolean) => {
        if (isSelect) {
            setSelectedRows([...selectedRows, row.id])
        } else {
            const index = selectedRows.indexOf(row.id)
            if (index > -1) {
                selectedRows.splice(index, 1)
            }
            setSelectedRows([])
        }
    }

    const handleOnSelectAll = (isSelect: boolean, rows: any) => {
        const ids = rows.map((r: { id: number }) => r.id)
        if (isSelect) {
            setSelectedRows(ids)
        } else {
            setSelectedRows([])
        }
    }

    useEffect(() => {
        let customersData = customers
            ?.filter((c: Record<string, any>) => c.is_credit_user)
            ?.map((customer: any) => ({
                value: customer.id,
                label: customer?.user_pin + " " + customer?.name
            }))
        setCustomerList(customersData)
    }, [customers])

    const invoiceParcel = async () => {
        try {
            const res = await api.parcel.invoiceParcel(selectedRows)

            if (res?.status) {
                setSelectedRows([])
                dispatch(
                    loadCreditParcels({
                        page,
                        pageSize,
                        warehouse,
                        invoice_type,
                        customer,
                        parcelSearch
                    })
                )
            } else {
                setError(res?.error || res?.message || "")
            }
        } catch (error) {
            console.error(error)
            //setError(error)
        }
    }

    let handleChange = (e: any) => {
        setCustomerId(e.target.value)
    }

    useEffect(() => {
        if (creditParcels?.length === 0) {
            dispatch(
                loadCreditParcels({
                    page,
                    pageSize,
                    warehouse,
                    invoice_type,
                    customer,
                    parcelSearch
                })
            )
            dispatch(loadMarkReasons())
            dispatch(loadParcelDestinations())
            dispatch(loadParcelStatuses())
        }
    }, [])

    const onSearchClick = () => {
        dispatch(
            loadCreditParcels({
                page,
                pageSize,
                warehouse,
                invoice_type,
                customer,
                parcelSearch
            })
        )
    }
    useEffect(() => {
        dispatch(loadParcelDestinations())
        dispatch(loadParcelStatuses())
        dispatch(loadMarkReasons())
        dispatch(
            loadCreditParcels({
                page,
                pageSize,
                warehouse,
                invoice_type,
                customer,
                parcelSearch
            })
        )
        dispatch(supplierAutocomplete({}))
    }, [page, pageSize, warehouse, invoice_type, customer])

    useEffect(() => {
        const dateObj =
            startDate && endDate
                ? moment(startDate).format("YYYY-MM-DD") + "|" + moment(endDate).format("YYYY-MM-DD")
                : ""
        setParcelSearch({ ...parcelSearch, date: dateObj })
    }, [endDate])

    useEffect(() => {
        dispatch(
            loadCreditParcels({
                page,
                pageSize,
                warehouse,
                invoice_type,
                customer,
                parcelSearch
            })
        )
    }, [parcelSearch.date])

    return (
        <>
            {loading || !userInfo?.accessToken ? (
                <div className="loader row justify-content-center">
                    <ScaleLoader color="#15397F" width={4} height={40} />
                </div>
            ) : (
                <div className="parcels">
                    <Header
                        title="Credit Parcels"
                        records={creditParcels?.length + creditInvoicedParcels?.length}
                        data={creditParcels}
                        name="creditParcels">
                        <div className="d-flex">
                            <DateRangePickerCustom
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        </div>
                    </Header>
                    <div className="row d-flex">
                        <div className="col-10">&nbsp;</div>
                        <div className="col-auto w-30 justify-content-end align-items-end py-2">
                            <Form.Select
                                id="customers"
                                name="customers"
                                className="form-control select-column w-auto"
                                onChange={(e) => handleChange(e)}>
                                <option value={""}>{t("Account_Id")}</option>
                                {CustomerList?.map((customers: any) => (
                                    <option
                                        key={customers.value}
                                        value={customers.value}
                                        selected={customers?.value == customer}>
                                        {t(`${customers.label}`)}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    </div>
                    <div className="row  d-flex tabs-data px-2">
                        {show && error && error !== "" && (
                            <Alert variant="danger" onClose={() => setShow(false)} dismissible>
                                {t(error)}
                            </Alert>
                        )}

                        <Tabs defaultActiveKey="un-invoiced" id="un-invoiced-tab" className="mt-4">
                            <Tab
                                eventKey="un-invoiced"
                                title={
                                    <>
                                        {t("un invoiced")}
                                        <Badge className="mx-2 px-2">{creditParcels.length}</Badge>
                                    </>
                                }>
                                <CreditParcelsTable
                                    setShow={setShow}
                                    creditParcels={creditParcels}
                                    setButtonId={setButtonId}
                                    handleOnSelect={handleOnSelect}
                                    handleOnSelectAll={handleOnSelectAll}
                                    selectedRows={selectedRows}
                                    invoiceParcel={invoiceParcel}
                                />
                            </Tab>

                            <Tab
                                eventKey="invoiced"
                                title={
                                    <>
                                        {t("Invoiced")}
                                        <Badge className="mx-2 px-2">{creditInvoicedParcels.length}</Badge>
                                    </>
                                }>
                                <InvoicedCreditParcels
                                    setShow={setShow}
                                    InvoicedCreditParcels={creditInvoicedParcels}
                                    setButtonId={setButtonId}
                                    handleOnSelect={handleOnSelect}
                                    handleOnSelectAll={handleOnSelectAll}
                                    selectedRows={selectedRows}
                                    invoiceParcel={invoiceParcel}
                                />
                            </Tab>
                        </Tabs>
                    </div>

                    {loading || customersLoading || !userInfo?.accessToken ? (
                        <div className="loader row justify-content-center">
                            <ScaleLoader color="#15397F" width={4} height={40} />
                        </div>
                    ) : (
                        <div className="row d-flex"></div>
                    )}
                </div>
            )}
        </>
    )
}

export default CreditParcels
